// src/components/AgendamentoModal.js
import React, { useState, useEffect } from 'react';

const API_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:3001/api' 
  : '/api';

function AgendamentoModal({ isOpen, onClose, oportunidade, agendamento, onSalvar }) {
  const [formData, setFormData] = useState({
    data_agendamento: '',
    hora_agendamento: '',
    tecnico_id: '',
    tecnico_nome: '',
    cliente_telefone: '',
    cliente_endereco: '',
    observacoes: '',
    notificar_tecnico: true
  });

  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      carregarTecnicos();
    }
  }, [isOpen]);

  useEffect(() => {
    if (agendamento) {
      setFormData({
        data_agendamento: agendamento.data_agendamento,
        hora_agendamento: agendamento.hora_agendamento.slice(0, 5),
        tecnico_id: agendamento.tecnico_id || '',
        tecnico_nome: agendamento.tecnico_nome || '',
        cliente_telefone: agendamento.cliente_telefone || oportunidade?.cliente_telefone || '',
        cliente_endereco: agendamento.cliente_endereco || oportunidade?.cliente_contato || '',
        observacoes: agendamento.observacoes || '',
        notificar_tecnico: true // Permitir notificar ao editar
      });
    } else {
      const amanha = new Date();
      amanha.setDate(amanha.getDate() + 1);
      const dataFormatada = amanha.toISOString().split('T')[0];
      
      setFormData({
        data_agendamento: dataFormatada,
        hora_agendamento: '08:00',
        tecnico_id: '',
        tecnico_nome: '',
        cliente_telefone: oportunidade?.cliente_telefone || '',
        cliente_endereco: oportunidade?.cliente_contato || '',
        observacoes: '',
        notificar_tecnico: true
      });
    }
  }, [agendamento, isOpen, oportunidade]);

  const carregarTecnicos = async () => {
    try {
      const response = await fetch(`${API_URL}/tecnicos`);
      const data = await response.json();
      
      if (data.success) {
        setTecnicos(data.tecnicos);
      } else {
        console.error('Erro ao carregar técnicos:', data.error);
      }
    } catch (error) {
      console.error('Erro ao carregar técnicos:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTecnicoChange = (e) => {
    const tecnicoId = e.target.value;
    const tecnico = tecnicos.find(t => t.id === parseInt(tecnicoId));
    
    setFormData(prev => ({
      ...prev,
      tecnico_id: tecnicoId,
      tecnico_nome: tecnico ? tecnico.nome : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.data_agendamento || !formData.hora_agendamento || !formData.tecnico_id) {
      alert('Por favor, preencha todos os campos obrigatórios!');
      return;
    }
    
    setLoading(true);

    try {
      const agendamentoSalvo = await onSalvar(formData);

      // NOTIFICAÇÃO WHATSAPP
      if (formData.tecnico_id && formData.notificar_tecnico && agendamentoSalvo?.id) {
        try {
          const endpoint = agendamento 
            ? '/tecnicos/notificar-alteracao' 
            : '/tecnicos/notificar-agendamento';
          
          const response = await fetch(`${API_URL}${endpoint}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              agendamento_id: agendamentoSalvo.id
            })
          });

          const data = await response.json();
          
          if (data.success) {
            const acao = agendamento ? 'alterado' : 'criado';
            alert(`✅ Agendamento ${acao} e técnico notificado via WhatsApp!`);
          } else {
            const acao = agendamento ? 'alterado' : 'criado';
            alert(`✅ Agendamento ${acao}! ⚠️ Não foi possível enviar WhatsApp.`);
          }
        } catch (error) {
          console.error('⚠️ Erro ao notificar técnico:', error);
          const acao = agendamento ? 'alterado' : 'criado';
          alert(`✅ Agendamento ${acao}! ⚠️ Erro ao enviar WhatsApp.`);
        }
      }

      onClose();
    } catch (error) {
      console.error('❌ ERRO AO SALVAR:', error);
      alert('Erro ao salvar agendamento: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-agendamento" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📅 {agendamento ? 'Editar' : 'Agendar'} Atendimento</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="agendamento-info">
            <p><strong>OS:</strong> #{oportunidade.os_id_gestao_click || oportunidade.os_numero || 'N/A'}</p>
            <p><strong>Cliente:</strong> {oportunidade.cliente_nome}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="data_agendamento">📅 Data do Agendamento *</label>
                <input
                  type="date"
                  id="data_agendamento"
                  name="data_agendamento"
                  value={formData.data_agendamento}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="hora_agendamento">⏰ Hora *</label>
                <input
                  type="time"
                  id="hora_agendamento"
                  name="hora_agendamento"
                  value={formData.hora_agendamento}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="tecnico_id">👷 Técnico Responsável *</label>
              <select
                id="tecnico_id"
                name="tecnico_id"
                value={formData.tecnico_id}
                onChange={handleTecnicoChange}
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Selecione um técnico...</option>
                {tecnicos.map(tecnico => (
                  <option key={tecnico.id} value={tecnico.id}>
                    {tecnico.nome} {tecnico.especialidades && `- ${tecnico.especialidades}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="cliente_telefone">📱 Telefone de Contato do Cliente</label>
              <input
                type="tel"
                id="cliente_telefone"
                name="cliente_telefone"
                value={formData.cliente_telefone}
                onChange={handleChange}
                placeholder="(16) 99999-9999"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Puxado do Gestão Click, pode ser editado se necessário
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="cliente_endereco">📍 Endereço do Cliente</label>
              <textarea
                id="cliente_endereco"
                name="cliente_endereco"
                value={formData.cliente_endereco}
                onChange={handleChange}
                placeholder="Rua, número, complemento, bairro, cidade..."
                rows="2"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Puxado do Gestão Click, pode ser editado se necessário
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="observacoes">📝 Observações (opcional)</label>
              <textarea
                id="observacoes"
                name="observacoes"
                value={formData.observacoes}
                onChange={handleChange}
                placeholder="Informações adicionais sobre o agendamento..."
                rows="3"
                disabled={loading}
              />
            </div>

            {formData.tecnico_id && (
              <div className="form-group" style={{ marginTop: '15px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px',
                  background: '#f0f9ff',
                  border: '2px solid #bfdbfe',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="notificar_tecnico"
                    checked={formData.notificar_tecnico}
                    onChange={handleChange}
                    disabled={loading}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#1e40af'
                  }}>
                    📱 Notificar técnico via WhatsApp
                  </span>
                </label>
              </div>
            )}

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-cancelar" 
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="btn-confirmar"
                disabled={loading}
              >
                {loading ? '⏳ Salvando...' : `✅ ${agendamento ? 'Atualizar' : 'Confirmar'} Agendamento`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AgendamentoModal;